import { InlineNotice } from '../../../design-system';
import { DeleteOutlined, ImageOutlined, UploadOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import type { OrganizationSettings } from '../model/types';
import type { TenantSettingsPort } from '../port/TenantSettingsPort';
import { FormDialog, designTokens } from '../../../design-system';
type OrganizationLogoControlProps = {
  port: TenantSettingsPort;
  settings: OrganizationSettings;
  fixtureMode: boolean;
  readOnly: boolean;
  disabled: boolean;
  variant?: 'stacked' | 'inline';
  onChanged: (settings: OrganizationSettings) => void;
  onNotice: (message: string | undefined) => void;
};
const maxBytes = 8 << 20;
export function OrganizationLogoControl({
  port,
  settings,
  fixtureMode,
  readOnly,
  disabled,
  variant = 'stacked',
  onChanged,
  onNotice,
}: OrganizationLogoControlProps) {
  const inline = variant === 'inline';
  const inputRef = useRef<HTMLInputElement>(null);
  const [currentUrl, setCurrentUrl] = useState(settings.logoUrl);
  const [selectedFile, setSelectedFile] = useState<File>();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => {
    setCurrentUrl(settings.logoUrl);
  }, [settings.logoUrl]);
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );
  const choose = () => {
    if (!readOnly && !disabled) inputRef.current?.click();
  };
  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    onNotice(undefined);
    if (readOnly || disabled || !file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setError('JPG 또는 PNG 로고를 선택해 주세요.');
      return;
    }
    if (file.size > maxBytes) {
      setError('조직 로고는 8MB 이하로 선택해 주세요.');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const nextPreview = URL.createObjectURL(file);
    setSelectedFile(file);
    setPreviewUrl(nextPreview);
    setError(undefined);
    setPreviewOpen(true);
  };
  const closePreview = () => {
    if (busy) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(undefined);
    setPreviewUrl(undefined);
    setPreviewOpen(false);
  };
  const upload = () => {
    if (readOnly || disabled || !selectedFile || !previewUrl || busy) return;
    setBusy(true);
    setError(undefined);
    const fixtureUrl = fixtureMode ? URL.createObjectURL(selectedFile) : undefined;
    const request = fixtureMode
      ? Promise.resolve({
          ...settings,
          logoUrl: fixtureUrl as string,
          version: settings.version + 1,
          updatedAt: new Date().toISOString(),
        })
      : port.uploadOrganizationLogo(selectedFile);
    void request
      .then(
        (next) => {
          if (fixtureMode && currentUrl?.startsWith('blob:')) URL.revokeObjectURL(currentUrl);
          setCurrentUrl(next.logoUrl);
          setSelectedFile(undefined);
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          setPreviewUrl(undefined);
          setPreviewOpen(false);
          onNotice('조직 로고를 저장했습니다.');
          onChanged(next);
        },
        (reason: unknown) => {
          if (fixtureUrl) URL.revokeObjectURL(fixtureUrl);
          setError(reason instanceof Error ? reason.message : '조직 로고를 저장하지 못했습니다.');
        },
      )
      .finally(() => setBusy(false));
  };
  const remove = () => {
    if (readOnly || disabled || !currentUrl || busy) return;
    setBusy(true);
    setError(undefined);
    const request = fixtureMode
      ? Promise.resolve({
          ...settings,
          logoUrl: undefined,
          version: settings.version + 1,
          updatedAt: new Date().toISOString(),
        })
      : port.deleteOrganizationLogo();
    void request
      .then(
        (next) => {
          if (fixtureMode && currentUrl.startsWith('blob:')) URL.revokeObjectURL(currentUrl);
          setCurrentUrl(undefined);
          setDeleteOpen(false);
          onNotice('조직 로고를 삭제했습니다.');
          onChanged(next);
        },
        (reason: unknown) => {
          setError(reason instanceof Error ? reason.message : '조직 로고를 삭제하지 못했습니다.');
        },
      )
      .finally(() => setBusy(false));
  };
  return (
    <>
      <Stack
        spacing={1.25}
        sx={{ width: inline ? '100%' : { xs: '100%', sm: 180 }, alignItems: inline ? 'stretch' : { xs: 'stretch', sm: 'center' } }}
      >
        <Stack
          direction={inline ? 'row' : 'column'}
          spacing={inline ? 2 : 1.25}
          sx={{ width: '100%', alignItems: inline ? 'center' : { xs: 'stretch', sm: 'center' } }}
        >
          <Box
            data-testid="organization-logo-preview"
            sx={{
              width: inline ? 72 : 112,
              height: inline ? 72 : 112,
              flexShrink: 0,
              alignSelf: inline ? 'auto' : { xs: 'center', sm: 'auto' },
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: designTokens.color.strongDivider,
              borderRadius: inline ? designTokens.radius.navigation : designTokens.radius.surface,
              bgcolor: designTokens.color.canvas,
            }}
          >
            {currentUrl ? (
              <Box
                component="img"
                src={currentUrl}
                alt={`${settings.displayName} 조직 로고`}
                sx={{ width: '82%', height: '82%', objectFit: 'contain' }}
              />
            ) : (
              <ImageOutlined
                aria-hidden
                sx={{ fontSize: inline ? 28 : 38, color: designTokens.color.faintText }}
              />
            )}
          </Box>
          <Stack spacing={inline ? 0.75 : 1.25} sx={{ minWidth: 0, width: inline ? 'auto' : '100%', flex: inline ? 1 : undefined }}>
            {!readOnly && (
              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap={inline}
                sx={{ justifyContent: inline ? 'flex-start' : 'center', flexWrap: inline ? 'wrap' : 'nowrap' }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={busy ? <CircularProgress size={14} /> : <UploadOutlined />}
                  onClick={choose}
                  disabled={disabled || busy}
                >
                  {currentUrl ? '로고 변경' : '로고 업로드'}
                </Button>
                {currentUrl ? (
                  <IconButton
                    size="small"
                    color="inherit"
                    aria-label="조직 로고 삭제"
                    onClick={() => { onNotice(undefined); setDeleteOpen(true); }}
                    disabled={disabled || busy}
                  >
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                ) : null}
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: inline ? 'left' : 'center' }}>
              JPG 또는 PNG · 최대 8MB
            </Typography>
          </Stack>
        </Stack>
        {error ? (
          <InlineNotice
            severity="error" announcement="polite"
            density="compact"
          >
            {error}
          </InlineNotice>
        ) : null}
      </Stack>
      {!readOnly && (
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          aria-label="조직 로고 파일 선택"
          hidden
          onChange={onFile}
        />
      )}
      {!readOnly && (
        <FormDialog
          open={previewOpen}
          onClose={closePreview}
          onSubmit={upload}
          title="조직 로고 확인"
          cancelLabel="다시 선택"
          submitLabel="이 로고 사용"
          busy={busy}
          busyLabel="저장 중…"
          submitDisabled={!selectedFile || !previewUrl}
          maxWidth="xs"
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              pt: 1,
            }}
          >
            <Box
              sx={{
                width: 220,
                height: 160,
                display: 'grid',
                placeItems: 'center',
                border: '1px solid',
                borderColor: designTokens.color.divider,
                borderRadius: designTokens.radius.surface,
                bgcolor: designTokens.color.canvas,
              }}
            >
              <Box
                component="img"
                src={previewUrl}
                alt="선택한 조직 로고 미리보기"
                sx={{
                  width: '86%',
                  height: '86%',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: 'center',
              }}
            >
              투명 여백과 가로세로 비율을 유지해 저장합니다. 이미지 메타데이터는 제거됩니다.
            </Typography>
            {error ? (
              <InlineNotice
                severity="error" announcement="polite"
              >
                {error}
              </InlineNotice>
            ) : null}
          </Stack>
        </FormDialog>
      )}
      {!readOnly && (
        <FormDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onSubmit={remove}
          title="조직 로고 삭제"
          cancelLabel="취소"
          submitLabel="로고 삭제"
          busy={busy}
          busyLabel="삭제 중…"
          submitColor="error"
          maxWidth="xs"
        >
          <InlineNotice severity="warning">현재 로고를 삭제합니다. 새 로고는 언제든 다시 등록할 수 있습니다.</InlineNotice>
        </FormDialog>
      )}
    </>
  );
}
